import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';
import { PropertyDetailSheet, type PropertyDetail } from '@/components/cleaner/PropertyDetailSheet';
import { useCleanerPropertyDetail } from '@/hooks/useCleanerPropertyDetail';

interface PropertyNavigationState {
  property?: PropertyDetail;
}

export const PropertyDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { propertyId } = useParams<{ propertyId: string }>();
  const { currentOrgId } = useRole();

  const initialProperty = (location.state as PropertyNavigationState | null)?.property ?? null;
  const initialMatchesUrl =
    initialProperty && propertyId && initialProperty.id === propertyId ? initialProperty : null;

  const {
    data: queryProperty,
    isPending,
    isError,
    error: queryError,
    refetch,
  } = useCleanerPropertyDetail(propertyId, currentOrgId);

  const resolvedFromQuery =
    queryProperty && propertyId && queryProperty.id === propertyId ? queryProperty : null;
  const property = resolvedFromQuery ?? initialMatchesUrl ?? null;

  const queryEnabled = Boolean(propertyId && currentOrgId);
  const showFullPageLoader = queryEnabled && isPending && !property;

  if (!propertyId || !currentOrgId) {
    return null;
  }

  if (showFullPageLoader) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Odzyskiwanie danych...</p>
        </div>
      </div>
    );
  }

  if (isError && !property) {
    const message =
      queryError instanceof Error ? queryError.message : 'Nie udało się załadować danych budynku.';
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
              onClick={() => void refetch()}
            >
              Spróbuj ponownie
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-md border border-border"
              onClick={() => navigate('/')}
            >
              Wróć do listy budynków
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">Brak danych budynku.</p>
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => navigate('/')}
          >
            Wróć do listy budynków
          </button>
        </div>
      </div>
    );
  }

  return (
    <PropertyDetailSheet
      property={property}
      open
      onOpenChange={() => navigate('/')}
      embedded
      embeddedClassName="animate-in slide-in-from-right duration-300 fade-in fill-mode-both min-h-screen bg-canvas flex flex-col w-full absolute top-0 left-0 z-40"
      onWorkStarted={() => {
        void refetch();
      }}
    />
  );
};
